import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormHelperText,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  Popover,
  Radio,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { placeholderText } from "../../constants/colors";
import CommonTable from "../common/table/commonTable";
import CommonDialog from "../common/CommonDialog";
import { createHeaderWithSort, checkboxColumn } from "../common/table/columns";
import "../common/table/commonTable.css";
import {
  fetchCagUploadRows,
  deleteCagUploadRows,
  updateCagUploadRow,
  commitCagUploadRows,
  downloadCagTemplate,
  uploadCagFile,
  getCagConfiguration,
} from "../../api/cagConfigurations";
import { Upload, Download, AlertTriangle, Check, Trash2 } from "lucide-react";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { formatDateOnly } from "../../utils/formatDateTime";
import {
  capitalizeFirstLetter,
  formatVendorName,
} from "../../utils/capitalize";
import { useLoading } from "../../context/LoadingContext";
import ProgramIcon from "../dashboard/ProgramIcon";

const PROGRAM_DRUG_LIST_MAP = {
  "GLP-1": ["ECM", "LRX_ASSIST", "LUCYRX_WEIGHT MGMT", "TOP TIER EXPANDED"],
  Biosimilars: ["TOP TIER EXPANDED", "Orencia"],
  ECMP: ["ECM"],
  PAP: ["LRX_ASSIST_NON SPECIALTY"],
  "Women's Health Program": ["Abiraterone Acetate"],
  "International Sourcing": ["ECM", "LUCYRX_WEIGHT MGMT", "TOP TIER EXPANDED"],
  "Copay Assist": ["LRX_ASSIST", "ECM", "Orencia"],
  Diabetes: ["ECM", "LRX_ASSIST"],
};

const StagingField = ({
  label,
  name,
  value,
  handleChange,
  disabled,
  type = "text",
  placeholder,
  error,
  helperText,
  highlighted,
  autoFocus,
  sx = {},
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (highlighted && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus?.();
        inputRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "nearest",
        });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [highlighted]);

  return (
    <Box flex={1} minWidth={0}>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "35px",
          color: "#403C6F",
        }}
      >
        {label}
      </Typography>
      <TextField
        fullWidth
        name={name}
        inputRef={inputRef}
        autoFocus={autoFocus || highlighted}
        value={value ?? ""}
        onChange={handleChange}
        placeholder={placeholder}
        size="small"
        disabled={disabled}
        error={Boolean(error)}
        helperText={helperText}
        type={type}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "45px",
            borderRadius: "8px",
            backgroundColor: "#fff",
            "& fieldset": {
              borderColor: error ? "#f44336" : "#E0E0E0",
            },
            "&:hover fieldset": {
              borderColor: error ? "#f44336" : "#BDBDBD",
            },
            "&.Mui-focused fieldset": {
              borderColor: error ? "#f44336" : "#3f51b5",
            },
          },
          "& .MuiFormHelperText-root": {
            fontSize: "12px",
            mt: "4px",
            ml: "4px",
            color: "#d32f2f",
          },
          ...(disabled
            ? {
                backgroundColor: "#EFEFEF",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#EFEFEF",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#E0E0E0",
                  },
                },
              }
            : {}),
          ...sx,
        }}
      />
    </Box>
  );
};

StagingField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  highlighted: PropTypes.bool,
  autoFocus: PropTypes.bool,
  sx: PropTypes.object,
};

const StagingDateField = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  error = false,
  helperText = "",
  highlighted = false,
  sx = {},
}) => {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handleScroll = (event) => {
      if (
        event?.target?.closest?.(".MuiPickersPopper-root") ||
        event?.target?.closest?.(".MuiYearCalendar-root") ||
        event?.target?.closest?.(".MuiDateCalendar-root") ||
        event?.target?.closest?.(".MuiPickersLayout-root") ||
        event?.target?.classList?.contains?.("MuiYearCalendar-root") ||
        event?.target?.classList?.contains?.("MuiDateCalendar-root")
      ) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (highlighted && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus?.();
        inputRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "nearest",
        });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [highlighted]);

  const dayjsValue = value ? dayjs(value) : null;
  const isValidDate = dayjsValue?.isValid();

  return (
    <Box flex={1} minWidth={0}>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "35px",
          color: "#403C6F",
        }}
      >
        {label}
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          maxDate={dayjs("2100-12-31")}
          minDate={dayjs("1900-01-01")}
          value={isValidDate ? dayjsValue : null}
          onChange={(newValue) => {
            const formatted = newValue?.isValid()
              ? newValue.format("YYYY-MM-DD")
              : "";
            onChange(formatted);
          }}
          format="MM/DD/YYYY"
          disabled={disabled}
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
              name,
              inputRef,
              placeholder: "MM/DD/YYYY",
              error: Boolean(error),
              helperText: helperText,
              sx: {
                "& .MuiOutlinedInput-root": {
                  height: "45px",
                  borderRadius: "8px",
                  backgroundColor: disabled ? "#EFEFEF" : "#fff",
                  "& fieldset": {
                    borderColor: error ? "#f44336" : "#E0E0E0",
                  },
                  "&:hover fieldset": {
                    borderColor: error ? "#f44336" : "#BDBDBD",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: error ? "#f44336" : "#3f51b5",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: disabled ? "#999893" : "#0e1324",
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  mt: "4px",
                  ml: "4px",
                  color: "#d32f2f",
                },
                "& .MuiIconButton-root": {
                  color: "#403C6F",
                  padding: "4px",
                },
                ...sx,
              },
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

StagingDateField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  highlighted: PropTypes.bool,
  sx: PropTypes.object,
};

const StagingSelectField = ({
  label,
  name,
  value,
  onChange,
  disabled,
  error,
  helperText,
  highlighted,
  placeholder,
  multiple = false,
  options = [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ],
  sx = {},
}) => {
  const triggerRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (highlighted && triggerRef.current) {
      const timer = setTimeout(() => {
        triggerRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "nearest",
        });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [highlighted]);

  const normalizedOptions = useMemo(() => {
    return (options || []).map((opt) =>
      typeof opt === "string" ? { label: opt, value: opt } : opt,
    );
  }, [options]);

  const multipleValues = useMemo(() => {
    if (!multiple || !value || value === "-") return [];
    if (Array.isArray(value)) return value;
    return String(value)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [value, multiple]);

  const singleValue = useMemo(() => {
    if (multiple || value === undefined || value === null) return "";
    const str = String(value).trim();
    if (str.toLowerCase() === "yes" || str.toLowerCase() === "true")
      return "Yes";
    if (str.toLowerCase() === "no" || str.toLowerCase() === "false")
      return "No";
    return str;
  }, [value, multiple]);

  const selectedValues = multiple ? multipleValues : singleValue;

  const handleToggle = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSingleSelect = (optValue) => {
    onChange(optValue);
    setAnchorEl(null);
  };

  const handleMultipleSelect = (optValue) => {
    const isSelected = selectedValues.some(
      (v) => String(v).toLowerCase() === String(optValue).toLowerCase(),
    );
    let next;
    if (isSelected) {
      next = selectedValues.filter(
        (v) => String(v).toLowerCase() !== String(optValue).toLowerCase(),
      );
    } else {
      next = [...selectedValues, optValue];
    }
    onChange(next.length > 0 ? next.join(", ") : "-");
  };

  const handleSelectAll = () => {
    if (selectedValues.length === normalizedOptions.length) {
      onChange("-");
    } else {
      onChange(normalizedOptions.map((o) => o.value).join(", "));
    }
  };

  const renderDisplay = () => {
    if (multiple) {
      if (selectedValues.length === 0) {
        return (
          <Typography
            sx={{
              color: "#999893",
              fontWeight: 400,
              fontSize: "14px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {placeholder || `Select ${label}`}
          </Typography>
        );
      }
      if (selectedValues.length <= 2) {
        return (
          <Box display="flex" gap="4px" flexWrap="nowrap" overflow="hidden">
            {selectedValues.map((val) => (
              <Chip
                key={val}
                label={val}
                size="small"
                sx={{
                  bgcolor: "#EBF3FB",
                  color: "#1E293B",
                  fontSize: "12px",
                  height: "24px",
                  maxWidth: "140px",
                }}
              />
            ))}
          </Box>
        );
      }
      return (
        <Box display="flex" gap="4px" alignItems="center">
          <Chip
            label={selectedValues[0]}
            size="small"
            sx={{
              bgcolor: "#EBF3FB",
              color: "#1E293B",
              fontSize: "12px",
              height: "24px",
              maxWidth: "130px",
            }}
          />
          <Chip
            label={`+${selectedValues.length - 1}`}
            size="small"
            sx={{
              bgcolor: "#F1F5F9",
              color: "#475569",
              fontSize: "12px",
              height: "24px",
              fontWeight: 600,
            }}
          />
        </Box>
      );
    }

    const selectedOpt = normalizedOptions.find(
      (opt) =>
        String(opt.value).toLowerCase() ===
        String(selectedValues).toLowerCase(),
    );
    const fallbackLabel =
      selectedValues && selectedValues !== "-" ? selectedValues : "";
    const displayLabel = selectedOpt ? selectedOpt.label : fallbackLabel;
    const activeColor = disabled ? "#999893" : "#0e1324";

    return (
      <Typography
        sx={{
          color: displayLabel ? activeColor : "#999893",
          fontWeight: 400,
          fontSize: "14px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {displayLabel || placeholder || `Select ${label}`}
      </Typography>
    );
  };

  let borderColor = "#E0E0E0";
  if (error) borderColor = "#f44336";
  else if (open) borderColor = "#3f51b5";

  let hoverBorderColor = "#BDBDBD";
  if (disabled) hoverBorderColor = "#E0E0E0";
  else if (error) hoverBorderColor = "#f44336";

  return (
    <Box flex={1} minWidth={0}>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "35px",
          color: "#403C6F",
        }}
      >
        {label}
      </Typography>
      <Box
        ref={triggerRef}
        onMouseDown={handleToggle}
        sx={{
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          px: "14px",
          gap: "10px",
          height: "45px",
          background: disabled ? "#EFEFEF" : "#FFFFFF",
          border: `1px solid ${borderColor}`,
          borderRadius: "8px",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "border-color 0.2s",
          "&:hover": {
            borderColor: hoverBorderColor,
          },
          ...sx,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexWrap: "nowrap",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {renderDisplay()}
        </Box>
        <ExpandMoreIcon
          sx={{
            color: "#6E6D92",
            fontSize: 20,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              bgcolor: "#6E6D92",
              color: "#fff",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
              minWidth: triggerRef.current?.clientWidth || 200,
              maxWidth: 360,
              maxHeight: 280,
              overflowY: "auto",
            },
          },
        }}
      >
        <Box sx={{ p: "8px" }}>
          {multiple && normalizedOptions.length > 0 && (
            <MenuItem
              onClick={handleSelectAll}
              sx={{
                minHeight: 36,
                height: 38,
                justifyContent: "space-between",
                bgcolor:
                  selectedValues.length === normalizedOptions.length
                    ? "rgba(255,255,255,0.12)"
                    : "transparent",
                borderRadius: "4px",
                px: "12px",
                mb: "2px",
                "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                transition: "background-color 0.2s",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#fff",
                  fontWeight: 600,
                  flex: 1,
                }}
              >
                All
              </Typography>
              <Checkbox
                checked={selectedValues.length === normalizedOptions.length}
                indeterminate={
                  selectedValues.length > 0 &&
                  selectedValues.length < normalizedOptions.length
                }
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  "&.Mui-checked": { color: "#fff" },
                  "&.MuiCheckbox-indeterminate": { color: "#fff" },
                  p: 0,
                }}
              />
            </MenuItem>
          )}

          {normalizedOptions.length === 0 && (
            <Typography
              sx={{
                textAlign: "center",
                p: 2,
                fontSize: "13px",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              No options available
            </Typography>
          )}

          {normalizedOptions.map((opt, idx) => {
            const isSelected = multiple
              ? selectedValues.some(
                  (v) =>
                    String(v).toLowerCase() === String(opt.value).toLowerCase(),
                )
              : String(opt.value).toLowerCase() ===
                String(selectedValues).toLowerCase();

            return (
              <MenuItem
                key={`${opt.value}-${idx}`}
                onClick={() =>
                  multiple
                    ? handleMultipleSelect(opt.value)
                    : handleSingleSelect(opt.value)
                }
                sx={{
                  minHeight: 36,
                  height: 38,
                  justifyContent: "space-between",
                  bgcolor: isSelected
                    ? "rgba(255,255,255,0.12)"
                    : "transparent",
                  borderRadius: "4px",
                  px: "12px",
                  mb: idx === normalizedOptions.length - 1 ? 0 : "2px",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                  transition: "background-color 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Tooltip title={opt.label} placement="top-start" arrow>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#fff",
                      fontWeight: isSelected ? 600 : 400,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                    }}
                  >
                    {opt.label}
                  </Typography>
                </Tooltip>
                {multiple ? (
                  <Checkbox
                    checked={isSelected}
                    size="small"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      "&.Mui-checked": { color: "#fff" },
                      p: 0,
                    }}
                  />
                ) : (
                  <Radio
                    checked={isSelected}
                    size="small"
                    icon={
                      <RadioButtonUncheckedIcon
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          fontSize: 18,
                        }}
                      />
                    }
                    checkedIcon={
                      <RadioButtonCheckedIcon
                        sx={{ color: "#fff", fontSize: 18 }}
                      />
                    }
                    sx={{ p: 0 }}
                  />
                )}
              </MenuItem>
            );
          })}
        </Box>
      </Popover>

      {helperText && (
        <FormHelperText
          sx={{
            fontSize: "12px",
            mt: "4px",
            ml: "4px",
            color: "#d32f2f",
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

StagingSelectField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  highlighted: PropTypes.bool,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.any.isRequired,
      }),
    ]),
  ),
  sx: PropTypes.object,
};

const MappingDetailModal = ({ open, onClose, data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!data) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            width: isMobile ? "95%" : "auto",
            minWidth: isMobile ? "95%" : "40%",
            p: isMobile ? "12px" : "20px",
            bgcolor: "#F7F8FB",
            m: isMobile ? 1 : 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          mb: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: isMobile ? "18px" : "20px",
            color: "#10100F",
          }}
        >
          Program & Drug List Mapping
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#10100F" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <TableContainer
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            maxHeight: isMobile ? "60vh" : "70vh",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#403C6F",
                    bgcolor: "#CFCEDA",
                    border: "none",
                    width: "50%",
                    borderRight: "1px solid white",
                    p: isMobile ? 1.5 : 2,
                  }}
                >
                  Programs
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#403C6F",
                    bgcolor: "#CFCEDA",
                    border: "none",
                    width: "48%",
                    p: isMobile ? 1.5 : 2,
                  }}
                >
                  Drug List
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.programs || []).map((prog) => (
                <TableRow
                  key={prog.name}
                  sx={{
                    height: isMobile ? "auto" : "72px",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <TableCell
                    sx={{
                      borderBottom: "2px solid #F7F8FB",
                      borderRight: "1px solid #F7F8FB",
                      verticalAlign: "middle",
                      p: isMobile ? 1.5 : 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        px: "12px",
                        py: "6px",
                        borderRadius: "94px",
                      }}
                    >
                      <ProgramIcon name={prog.name} iconColor="#403C6F" />
                      <Typography
                        sx={{
                          fontWeight: 400,
                          fontSize: "14px !important",
                          color: "#403C6F",
                        }}
                      >
                        {prog.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: "2px solid #F7F8FB",
                      verticalAlign: "middle",
                      p: isMobile ? 1.5 : 2,
                    }}
                  >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {(prog.drugLists || []).map((dl) => (
                        <Chip
                          key={dl.label}
                          label={dl.label}
                          size="small"
                          sx={{
                            height: "26px",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#403C6F",
                            bgcolor: "rgba(64, 60, 111, 0.05)",
                            borderRadius: "4px",
                          }}
                        />
                      ))}
                      {(!prog.drugLists || prog.drugLists.length === 0) && "-"}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

MappingDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
};

// Program and Drug Lists Configuration
export const PROGRAM_CONFIG = [
  {
    name: "Copay Assist",
    statusKey: "copayAssistStatus",
    drugListKey: "copayAssistDrugLists",
  },
  {
    name: "ECMP",
    statusKey: "ecmpStatus",
    drugListKey: "ecmpDrugLists",
  },
  {
    name: "GLP-1",
    statusKey: "glp1Status",
    drugListKey: "glp1DrugLists",
  },
  {
    name: "International Sourcing",
    statusKey: "intlSourcingStatus",
    drugListKey: "intlSourcingDrugLists",
  },
  {
    name: "PAP",
    statusKey: "papStatus",
    drugListKey: "papDrugLists",
  },
  {
    name: "Biosimilars",
    statusKey: "biosimilarsStatus",
    drugListKey: "biosimilarsDrugLists",
  },
  {
    name: "Women's Health",
    statusKey: "womensHealthStatus",
    drugListKey: "womensHealthDrugLists",
  },
  {
    name: "Diabetes",
    statusKey: "diabetesStatus",
    drugListKey: "diabetesDrugLists",
  },
];

// Staging Table Columns Configuration
export const STAGING_COLUMNS_CONFIG = [
  {
    id: "org",
    header: "Org",
    isCombined: true,
    primaryKey: "orgName",
    secondaryKey: "orgCode",
    minWidth: 160,
  },
  {
    id: "carrier",
    header: "Carrier",
    isCombined: true,
    primaryKey: "carrierName",
    secondaryKey: "carrierId",
    minWidth: 200,
  },
  {
    id: "parentAccount",
    header: "Parent Account",
    isCombined: true,
    primaryKey: "parentAccountName",
    secondaryKey: "parentAccountId",
    minWidth: 190,
  },
  {
    id: "group",
    header: "Group",
    isCombined: true,
    primaryKey: "groupName",
    secondaryKey: "groupNumber",
    minWidth: 220,
  },
  {
    id: "groupStartDate",
    header: "Group Start Date",
    accessorKey: "groupStartDate",
    minWidth: 150,
  },
  {
    id: "groupEndDate",
    header: "Group End Date",
    accessorKey: "groupEndDate",
    minWidth: 150,
  },
  {
    id: "careGuideEffectiveDate",
    header: "Care Guide Effective Date",
    accessorKey: "careGuideEffectiveDate",
    minWidth: 190,
  },
  { id: "programs", header: "Programs", isPrograms: true, minWidth: 320 },
  { id: "drugList", header: "Drug List", isDrugList: true, minWidth: 350 },
  {
    id: "copayAssistVendor",
    header: "Copay Assist Vendor",
    accessorKey: "copayAssistVendor",
    minWidth: 170,
  },
  {
    id: "intlSourcingVendor",
    header: "International Sourcing Vendor",
    accessorKey: "intlSourcingVendor",
    minWidth: 200,
  },
  {
    id: "pcaDirective",
    header: "PCA Directive",
    accessorKey: "pcaDirective",
    minWidth: 180,
  },
];

// Helper to check if a specific field has a validation error
const getFieldError = (errors, columnKey) => {
  if (!errors || !Array.isArray(errors)) return null;
  const upperSnake = columnKey.replaceAll(/([A-Z])/g, "_$1").toUpperCase();
  return errors.find(
    (e) =>
      e.field === upperSnake ||
      e.field?.toUpperCase() === columnKey.toUpperCase() ||
      e.field?.replaceAll("_", "").toUpperCase() === columnKey.toUpperCase(),
  );
};

const extractRows = (data) => {
  const rows = Array.isArray(data)
    ? data
    : data?.content || data?.rows || data?.data || [];
  return Array.isArray(rows) ? rows : [];
};

const findProgramConfig = (cagConfigSchema, progName, extraKeywords) => {
  const nameB = progName.toLowerCase();
  return cagConfigSchema?.programConfigs?.find((p) => {
    const nameA = (p.programName || p.program || "").toLowerCase();
    return (
      nameA === nameB ||
      extraKeywords.some((k) => nameB.includes(k) && nameA.includes(k))
    );
  });
};

const resolveInitialFocusField = (record) => {
  const firstErrField = record?.errors?.[0]?.field;
  if (!firstErrField) return null;
  const target = firstErrField.replaceAll("_", "").toLowerCase();
  const progMatch = PROGRAM_CONFIG.find(
    (p) =>
      p.statusKey.toLowerCase() === target ||
      p.drugListKey.toLowerCase() === target,
  );
  if (progMatch) {
    return progMatch.statusKey.toLowerCase() === target
      ? progMatch.statusKey
      : progMatch.drugListKey;
  }
  const foundCol = STAGING_COLUMNS_CONFIG.find(
    (c) =>
      c.accessorKey?.toLowerCase() === target ||
      c.id?.toLowerCase() === target ||
      c.primaryKey?.toLowerCase() === target ||
      c.secondaryKey?.toLowerCase() === target,
  );
  if (!foundCol) return null;
  if (foundCol.isCombined) {
    return foundCol.primaryKey.toLowerCase() === target
      ? foundCol.primaryKey
      : foundCol.secondaryKey;
  }
  return foundCol.accessorKey || null;
};

const buildEditFormData = (record) => ({
  orgCode: record.orgCode || "",
  orgName: record.orgName || "",
  carrierName: record.carrierName || "",
  carrierId: record.carrierId === undefined ? "" : record.carrierId,
  parentAccountId: record.parentAccountId || "",
  parentAccountName: record.parentAccountName || "",
  groupName: record.groupName || "",
  groupNumber: record.groupNumber || "",
  groupStartDate: record.groupStartDate || "",
  groupEndDate: record.groupEndDate || "",
  careGuideEffectiveDate: record.careGuideEffectiveDate || "",
  pcaDirectiveEnabled: record.pcaDirectiveEnabled || "Yes",
  pcaDirective: record.pcaDirective || "",
  copayAssistStatus: record.copayAssistStatus || "No",
  copayAssistDrugLists: record.copayAssistDrugLists || "-",
  copayAssistVendor: record.copayAssistVendor
    ? formatVendorName(record.copayAssistVendor)
    : "-",
  ecmpStatus: record.ecmpStatus || "No",
  ecmpDrugLists: record.ecmpDrugLists || "-",
  glp1Status: record.glp1Status || "No",
  glp1DrugLists: record.glp1DrugLists || "-",
  intlSourcingStatus: record.intlSourcingStatus || "No",
  intlSourcingDrugLists: record.intlSourcingDrugLists || "-",
  intlSourcingVendor: record.intlSourcingVendor
    ? formatVendorName(record.intlSourcingVendor)
    : "-",
  papStatus: record.papStatus || "No",
  papDrugLists: record.papDrugLists || "-",
  biosimilarsStatus: record.biosimilarsStatus || "No",
  biosimilarsDrugLists: record.biosimilarsDrugLists || "-",
  womensHealthStatus: record.womensHealthStatus || "No",
  womensHealthDrugLists: record.womensHealthDrugLists || "-",
  diabetesStatus: record.diabetesStatus || "No",
  diabetesDrugLists: record.diabetesDrugLists || "-",
});

const DATE_FIELD_KEYS = new Set([
  "groupStartDate",
  "groupEndDate",
  "careGuideEffectiveDate",
]);

const getCellDisplayValue = (val, error, accessorKey, isVendorField) => {
  if (val === null || val === undefined || val === "") return "-";
  if (DATE_FIELD_KEYS.has(accessorKey)) {
    const formattedDate = val && !error ? formatDateOnly(val) : null;
    return formattedDate || String(val);
  }
  if (isVendorField) return formatVendorName(String(val));
  return capitalizeFirstLetter(String(val));
};

const getTrimmedOrDash = (val) => {
  if (val === null || val === undefined) return "-";
  return String(val).trim() || "-";
};

const resolveTitle = (error, prefix, fallbackText) =>
  error.message
    ? `${prefix}${error.message} (Click to resolve)`
    : `Click to resolve ${fallbackText}`;

const truncateText = (text, limit) =>
  text.length > limit ? `${text.substring(0, limit)}...` : text;

const LINE_STYLES = {
  primary: { error: ["13px", 600], normal: ["14px", 500, "#10100F"] },
  secondary: { error: ["12px", 500], normal: ["12px", 400, "#6E6D92"] },
};

const tabButtonSx = (isActive, activeBg, hoverBg) => ({
  height: "36px",
  borderRadius: "20px",
  px: 2,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "14px",
  fontStyle: "normal",
  letterSpacing: "0px",
  bgcolor: isActive ? activeBg : "transparent",
  color: isActive ? "#FFFFFF" : "#66638C",
  "&:hover": {
    bgcolor: isActive ? hoverBg : "rgba(102, 112, 133, 0.08)",
  },
});

const deleteButtonSx = (isInactive) => ({
  height: "33px",
  borderRadius: "48px",
  border: "1px solid",
  borderColor: isInactive ? "#E4E7EC" : "#FDA29B",
  bgcolor: isInactive ? "#F8F9FA" : "#FEF3F2",
  color: isInactive ? "#98A2B3" : "#B42318",
  textTransform: "none",
  fontSize: "13px",
  fontWeight: 500,
  fontStyle: "normal",
  px: 2.5,
  boxShadow: "none",
  "&:hover": {
    bgcolor: "#FEE4E2",
    borderColor: "#F04438",
    color: "#B42318",
  },
  "&.Mui-disabled": {
    borderColor: "#E4E7EC",
    bgcolor: "#F8F9FA",
    color: "#98A2B3",
  },
});

const getLineDisplayValue = (val, isPrimary) => {
  if (val === null || val === undefined || val === "") return "-";
  return isPrimary ? capitalizeFirstLetter(String(val)) : String(val);
};

const isProgramActive = (row, p) => {
  const val = String(row[p.statusKey] || "")
    .trim()
    .toLowerCase();
  return val === "yes" || val === "true";
};

const collectUniqueDrugLists = (row) => {
  const list = [];
  const ignored = new Set(["", "-", "n/a", "none"]);
  PROGRAM_CONFIG.forEach((p) => {
    const val = row[p.drugListKey];
    if (!val || typeof val !== "string") return;
    val.split(",").forEach((item) => {
      const cleaned = item.trim();
      if (!ignored.has(cleaned.toLowerCase()) && !list.includes(cleaned)) {
        list.push(cleaned);
      }
    });
  });
  return list;
};

const toNumericId = (id) => {
  const num = Number(id);
  return Number.isNaN(num) ? id : num;
};

const toStringOrEmpty = (v) => (v === undefined || v === null ? "" : String(v));

const buildSavePayload = (editingRecord, f, formatDate) => ({
  id: toNumericId(editingRecord.id),
  orgCode: f.orgCode ?? "",
  orgName: f.orgName ?? "",
  carrierName: f.carrierName ?? "",
  carrierId: toStringOrEmpty(f.carrierId),
  parentAccountId: toStringOrEmpty(f.parentAccountId),
  parentAccountName: f.parentAccountName ?? "",
  groupName: f.groupName ?? "",
  groupNumber: f.groupNumber ?? "",
  groupStartDate: formatDate(f.groupStartDate) ?? "",
  groupEndDate: formatDate(f.groupEndDate) ?? "",
  careGuideEffectiveDate: formatDate(f.careGuideEffectiveDate) ?? "",
  pcaDirective: f.pcaDirective ?? "",
  pcaDirectiveEnabled: f.pcaDirectiveEnabled ?? "Yes",
  copayAssistStatus: f.copayAssistStatus ?? "No",
  copayAssistDrugLists: f.copayAssistDrugLists ?? "-",
  copayAssistVendor: f.copayAssistVendor
    ? formatVendorName(f.copayAssistVendor)
    : "-",
  ecmpStatus: f.ecmpStatus ?? "No",
  ecmpDrugLists: f.ecmpDrugLists ?? "-",
  glp1Status: f.glp1Status ?? "No",
  glp1DrugLists: f.glp1DrugLists ?? "-",
  intlSourcingStatus: f.intlSourcingStatus ?? "No",
  intlSourcingDrugLists: f.intlSourcingDrugLists ?? "-",
  intlSourcingVendor: f.intlSourcingVendor
    ? formatVendorName(f.intlSourcingVendor)
    : "-",
  papStatus: f.papStatus ?? "No",
  papDrugLists: f.papDrugLists ?? "-",
  biosimilarsStatus: f.biosimilarsStatus ?? "No",
  biosimilarsDrugLists: f.biosimilarsDrugLists ?? "-",
  womensHealthStatus: f.womensHealthStatus ?? "No",
  womensHealthDrugLists: f.womensHealthDrugLists ?? "-",
  diabetesStatus: f.diabetesStatus ?? "No",
  diabetesDrugLists: f.diabetesDrugLists ?? "-",
});

// Estimates the diagonal screen size in inches based on screen resolution and DPI
function getScreenInches() {
  const { width, height } = window.screen;
  const dpi = window.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

const ActionsHeader = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
    }}
  >
    {createHeaderWithSort("Actions", false)}
  </Box>
);

const ActionsCell = ({ row, column }) => {
  const { onResolve } = column.columnDef.meta;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}
    >
      <Button
        variant="outlined"
        color="error"
        size="small"
        onClick={() => onResolve(row.original)}
        sx={{
          color: (theme) => theme.palette.error?.main || "#D32F2F",
          borderColor: (theme) => theme.palette.error?.main || "#D32F2F",
          border: (theme) =>
            `1.5px solid ${theme.palette.error?.main || "#D32F2F"}`,
          borderRadius: "20px",
          fontWeight: 600,
          fontSize: "13px",
          textTransform: "none",
          px: 1.8,
          py: 0.4,
          height: "30px",
          bgcolor: "#FFFFFF",
          whiteSpace: "nowrap",
          boxShadow: "none",
          "&:hover": {
            borderColor: (theme) => theme.palette.error?.dark || "#B71C1C",
            border: (theme) =>
              `1.5px solid ${theme.palette.error?.dark || "#B71C1C"}`,
            bgcolor: "rgba(211, 47, 47, 0.06)",
            color: (theme) => theme.palette.error?.dark || "#B71C1C",
          },
        }}
      >
        Resolve Errors
      </Button>
    </Box>
  );
};

const CombinedCell = ({ row, column }) => {
  const { col, onResolve } = column.columnDef.meta;

  const primaryVal = row.original[col.primaryKey];
  const secondaryVal = row.original[col.secondaryKey];
  const primaryError = getFieldError(row.original.errors, col.primaryKey);
  const secondaryError = getFieldError(row.original.errors, col.secondaryKey);

  const renderLine = (val, error, fieldKey, isPrimary) => {
    const lineStyle = LINE_STYLES[isPrimary ? "primary" : "secondary"];
    const [errFontSize, errFontWeight] = lineStyle.error;
    const [fontSize, fontWeight, textColor] = lineStyle.normal;
    const displayVal = getLineDisplayValue(val, isPrimary);

    if (error) {
      return (
        <Tooltip
          title={resolveTitle(error, "", "validation error")}
          arrow
          placement="top"
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onResolve(row.original, fieldKey);
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              bgcolor: "#FEF3F2",
              border: "1px solid #FECDCA",
              borderRadius: "4px",
              px: 0.75,
              py: 0.1,
              maxWidth: "100%",
              cursor: "pointer",
              transition: "all 0.15s ease-in-out",
              "&:hover": {
                bgcolor: "#FEE4E2",
                borderColor: "#FDA29B",
              },
            }}
          >
            <AlertTriangle
              size={11}
              color="#F04438"
              style={{ flexShrink: 0 }}
            />
            <Typography
              sx={{
                color: "#B42318",
                fontSize: errFontSize,
                fontWeight: errFontWeight,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayVal}
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        title={displayVal !== "-" ? displayVal : ""}
        arrow
        placement="bottom-start"
        disableInteractive
      >
        <Typography
          sx={{
            fontSize,
            fontWeight,
            color: textColor,
            lineHeight: "140%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {displayVal}
        </Typography>
      </Tooltip>
    );
  };

  return (
    <Box
      className="cag-cell"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        minWidth: 0,
        py: 0.25,
      }}
    >
      {renderLine(primaryVal, primaryError, col.primaryKey, true)}
      {renderLine(secondaryVal, secondaryError, col.secondaryKey, false)}
    </Box>
  );
};

const ProgramsCell = ({ row, column }) => {
  const { onResolve, onOpenDetail } = column.columnDef.meta;

  const activeProgs = PROGRAM_CONFIG.filter((p) =>
    isProgramActive(row.original, p),
  );

  const statusErrors = PROGRAM_CONFIG.map((p) => ({
    prog: p,
    error: getFieldError(row.original.errors, p.statusKey),
  })).filter((item) => Boolean(item.error));

  if (statusErrors.length > 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          minWidth: 0,
          py: 0.25,
        }}
      >
        {statusErrors.map(({ prog, error }) => (
          <Tooltip
            key={prog.statusKey}
            title={resolveTitle(
              error,
              `${prog.name}: `,
              `${prog.name} validation error`,
            )}
            arrow
            placement="top"
          >
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onResolve(row.original, prog.statusKey);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#FEF3F2",
                border: "1px solid #FECDCA",
                borderRadius: "4px",
                px: 0.75,
                py: 0.2,
                cursor: "pointer",
                transition: "all 0.15s ease-in-out",
                "&:hover": {
                  bgcolor: "#FEE4E2",
                  borderColor: "#FDA29B",
                },
              }}
            >
              <AlertTriangle
                size={11}
                color="#F04438"
                style={{ flexShrink: 0 }}
              />
              <Typography
                sx={{
                  color: "#B42318",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {prog.name}: {row.original[prog.statusKey] || "Invalid"}
              </Typography>
            </Box>
          </Tooltip>
        ))}
        {activeProgs
          .filter(
            (p) => !statusErrors.some((e) => e.prog.statusKey === p.statusKey),
          )
          .map((prog) => (
            <Tooltip key={prog.name} title={prog.name} arrow>
              <Chip
                icon={
                  <ProgramIcon
                    name={prog.name}
                    iconColor="#403C6F"
                    customStyle={{
                      width: "16px",
                      height: "16px",
                      marginLeft: 6,
                      marginRight: 0,
                    }}
                  />
                }
                label={prog.name}
                size="small"
                sx={{
                  height: "26px !important",
                  fontSize: "12px",
                  fontWeight: 500,
                  bgcolor: "rgba(64, 60, 111, 0.05)",
                  color: "#403C6F",
                  borderRadius: "94px",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </Tooltip>
          ))}
      </Box>
    );
  }

  if (activeProgs.length === 0) {
    return (
      <Typography sx={{ color: "#667085", fontSize: "13px" }}>-</Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: "6px",
        maxHeight: "64px",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {activeProgs.slice(0, 3).map((prog) => (
        <Tooltip key={prog.name} title={prog.name} arrow>
          <Chip
            icon={
              <ProgramIcon
                name={prog.name}
                iconColor="#403C6F"
                customStyle={{
                  width: "18px",
                  height: "16px",
                  marginLeft: 8,
                  marginRight: 0,
                }}
              />
            }
            label={prog.name}
            size="small"
            sx={{
              height: "26px !important",
              fontSize: "13px",
              fontWeight: 500,
              bgcolor: "rgba(64, 60, 111, 0.05)",
              color: "#403C6F",
              borderRadius: "94px",
              maxWidth: "140px",
              flexShrink: 1,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              },
            }}
          />
        </Tooltip>
      ))}
      {activeProgs.length > 3 && (
        <Tooltip
          title={activeProgs
            .slice(3)
            .map((p) => p.name)
            .join(", ")}
          arrow
        >
          <Chip
            label={`+${activeProgs.length - 3}`}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetail(row.original);
            }}
            sx={{
              height: "24px",
              fontSize: "10.5px",
              fontWeight: 600,
              borderRadius: "4px",
              cursor: "pointer",
              bgcolor: "rgba(64, 60, 111, 0.05)",
              color: "#403C6F",
              flexShrink: 0,
              "&:hover": { bgcolor: "rgba(64, 60, 111, 0.1)" },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

const DrugListCell = ({ row, column }) => {
  const { onResolve, onOpenDetail } = column.columnDef.meta;

  const dlErrors = PROGRAM_CONFIG.map((p) => ({
    prog: p,
    error: getFieldError(row.original.errors, p.drugListKey),
  })).filter((item) => Boolean(item.error));

  const drugLists = collectUniqueDrugLists(row.original);

  if (dlErrors.length > 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          minWidth: 0,
          py: 0.25,
        }}
      >
        {dlErrors.map(({ prog, error }) => (
          <Tooltip
            key={prog.drugListKey}
            title={resolveTitle(
              error,
              `${prog.name} Drug List: `,
              `${prog.name} drug list validation error`,
            )}
            arrow
            placement="top"
          >
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onResolve(row.original, prog.drugListKey);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#FEF3F2",
                border: "1px solid #FECDCA",
                borderRadius: "4px",
                px: 0.75,
                py: 0.2,
                cursor: "pointer",
                transition: "all 0.15s ease-in-out",
                "&:hover": {
                  bgcolor: "#FEE4E2",
                  borderColor: "#FDA29B",
                },
              }}
            >
              <AlertTriangle
                size={11}
                color="#F04438"
                style={{ flexShrink: 0 }}
              />
              <Typography
                sx={{
                  color: "#B42318",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {prog.name}: {row.original[prog.drugListKey] || "Invalid"}
              </Typography>
            </Box>
          </Tooltip>
        ))}
        {drugLists.slice(0, 2).map((dl, idx) => (
          <Tooltip key={`${dl}-${idx}`} title={dl} arrow>
            <Chip
              label={truncateText(dl, 10)}
              size="small"
              sx={{
                height: "24px",
                fontSize: "13px",
                fontWeight: 500,
                bgcolor: "rgba(64, 60, 111, 0.05)",
                color: "#403C6F",
                borderRadius: "4px",
                maxWidth: "130px",
                flexShrink: 1,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                },
              }}
            />
          </Tooltip>
        ))}
        {drugLists.length > 2 && (
          <Tooltip title={drugLists.slice(2).join(", ")} arrow>
            <Chip
              label={`+${drugLists.length - 2}`}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetail(row.original);
              }}
              sx={{
                height: "22px",
                fontSize: "10.5px",
                fontWeight: 600,
                borderRadius: "4px",
                cursor: "pointer",
                bgcolor: "rgba(64, 60, 111, 0.05)",
                color: "#403C6F",
                border: "none",
                flexShrink: 0,
                "&:hover": { bgcolor: "rgba(64, 60, 111, 0.1)" },
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  if (drugLists.length === 0) {
    return (
      <Typography sx={{ color: "#667085", fontSize: "13px" }}>-</Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: "4px",
        maxHeight: "64px",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {drugLists.slice(0, 2).map((dl, idx) => (
        <Tooltip key={`${dl}-${idx}`} title={dl} arrow>
          <Chip
            label={truncateText(dl, 10)}
            size="small"
            sx={{
              height: "24px",
              fontSize: "13px",
              fontWeight: 500,
              bgcolor: "rgba(64, 60, 111, 0.05)",
              color: "#403C6F",
              borderRadius: "4px",
              maxWidth: "130px",
              flexShrink: 1,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              },
            }}
          />
        </Tooltip>
      ))}
      {drugLists.length > 2 && (
        <Tooltip title={drugLists.slice(2).join(", ")} arrow>
          <Chip
            label={`+${drugLists.length - 2}`}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetail(row.original);
            }}
            sx={{
              height: "22px",
              fontSize: "10.5px",
              fontWeight: 600,
              borderRadius: "4px",
              cursor: "pointer",
              bgcolor: "rgba(64, 60, 111, 0.05)",
              color: "#403C6F",
              border: "none",
              flexShrink: 0,
              "&:hover": { bgcolor: "rgba(64, 60, 111, 0.1)" },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

const GenericCell = ({ row, column }) => {
  const { col, onResolve } = column.columnDef.meta;
  const isVendorField =
    col.accessorKey === "copayAssistVendor" ||
    col.accessorKey === "intlSourcingVendor";

  const val = row.original[col.accessorKey];
  const error = getFieldError(row.original.errors, col.accessorKey);
  const displayVal = getCellDisplayValue(
    val,
    error,
    col.accessorKey,
    isVendorField,
  );

  if (error) {
    return (
      <Tooltip
        title={resolveTitle(error, "", "validation error")}
        arrow
        placement="top"
      >
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onResolve(row.original, col.accessorKey);
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            bgcolor: "#FEF3F2",
            border: "1px solid #FECDCA",
            borderRadius: "6px",
            px: 1,
            py: 0.3,
            maxWidth: "100%",
            cursor: "pointer",
            transition: "all 0.15s ease-in-out",
            "&:hover": {
              bgcolor: "#FEE4E2",
              borderColor: "#FDA29B",
              boxShadow: "0 1px 4px rgba(240, 68, 56, 0.25)",
            },
          }}
        >
          <AlertTriangle size={13} color="#F04438" style={{ flexShrink: 0 }} />
          <Typography
            sx={{
              color: "#B42318",
              fontSize: "13px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              userSelect: "none",
            }}
          >
            {displayVal}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  if (col.accessorKey === "pcaDirective" || col.id === "pcaDirective") {
    const rawVal = getTrimmedOrDash(val);

    if (rawVal === "-") {
      return (
        <Typography sx={{ color: "#667085", fontSize: "13px" }}>-</Typography>
      );
    }

    const CHAR_LIMIT = 64;
    const isTruncated = rawVal.length > CHAR_LIMIT;
    const textToDisplay = truncateText(rawVal, CHAR_LIMIT);

    if (isTruncated) {
      return (
        <Tooltip title={rawVal} arrow placement="top">
          <Typography
            sx={{
              color: "#10100F",
              fontSize: "13px",
              fontWeight: 400,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              cursor: "default",
              display: "inline-block",
              maxWidth: "100%",
            }}
          >
            {textToDisplay}
          </Typography>
        </Tooltip>
      );
    }

    return (
      <Typography
        sx={{
          color: "#10100F",
          fontSize: "13px",
          fontWeight: 400,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {textToDisplay}
      </Typography>
    );
  }

  if (displayVal === "Yes") {
    return (
      <Typography
        sx={{
          color: "#027A48",
          fontWeight: 600,
          fontSize: "13px",
          whiteSpace: "nowrap",
        }}
      >
        Yes
      </Typography>
    );
  }
  if (displayVal === "No") {
    return (
      <Typography
        sx={{
          color: "#667085",
          fontWeight: 500,
          fontSize: "13px",
          whiteSpace: "nowrap",
        }}
      >
        No
      </Typography>
    );
  }

  return (
    <Typography
      sx={{
        color: "#10100F",
        fontSize: "13px",
        fontWeight: 400,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
      title={displayVal !== "-" ? displayVal : undefined}
    >
      {displayVal}
    </Typography>
  );
};

const CagStagingEnvironment = ({ onBack, canWrite = true }) => {
  const [containerScale, setContainerScale] = useState(1);
  const resultsContainerRef = useRef(null);
  const [computedTableHeight, setComputedTableHeight] = useState(0);

  useEffect(() => {
    const inches = getScreenInches();
    setContainerScale(inches < 14 ? 0.9 : 1);
  }, []);

  useEffect(() => {
    const computeHeight = () => {
      const el = resultsContainerRef.current;
      if (!el) return;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const top = el.getBoundingClientRect().top;

      const isSmallLaptop =
        window.innerWidth <= 1440 && window.innerHeight <= 900;
      const extra = isSmallLaptop ? 24 : 0;
      const bottomPadding = isSmallLaptop ? 4 : 16;
      const h = Math.max(200, Math.floor(vh - top - bottomPadding + extra));
      setComputedTableHeight(h);
    };

    computeHeight();
    window.addEventListener("resize", computeHeight);
    const ro = new ResizeObserver(() => computeHeight());
    try {
      if (document.body) ro.observe(document.body);
    } catch (error) {
      console.debug("ResizeObserver loop limit exceeded:", error);
    }
    return () => {
      window.removeEventListener("resize", computeHeight);
      ro.disconnect?.();
    };
  }, []);

  // Staging state - purely API-driven
  const [cagConfigSchema, setCagConfigSchema] = useState(null);

  useEffect(() => {
    const fetchConfigSchema = async () => {
      try {
        const res = await getCagConfiguration();
        setCagConfigSchema(res?.data || null);
      } catch (err) {
        console.error("Failed to fetch CAG configuration schema:", err);
      }
    };
    fetchConfigSchema().catch((err) => console.error(err));
  }, []);

  const getProgramDrugListOptions = useMemo(() => {
    return (progName) => {
      const pc = findProgramConfig(cagConfigSchema, progName, [
        "women",
        "glp",
        "copay",
        "intl",
        "international",
      ]);
      if (Array.isArray(pc?.drugListConfigs) && pc.drugListConfigs.length > 0) {
        return pc.drugListConfigs.map((d) => d.drugListName);
      }
      return PROGRAM_DRUG_LIST_MAP[progName] || [];
    };
  }, [cagConfigSchema]);

  const getProgramVendorOptions = useMemo(() => {
    return (progName) => {
      const pc = findProgramConfig(cagConfigSchema, progName, [
        "copay",
        "intl",
        "international",
      ]);
      if (Array.isArray(pc?.vendorConfigs) && pc.vendorConfigs.length > 0) {
        return pc.vendorConfigs.map((v) => formatVendorName(v.vendorName));
      }
      return progName.toLowerCase().includes("copay")
        ? ["Ascella", "GRM"]
        : ["GRM"];
    };
  }, [cagConfigSchema]);

  const [goodRecords, setGoodRecords] = useState([]);
  const [badRecords, setBadRecords] = useState([]);
  const [duplicateRecords, setDuplicateRecords] = useState([]);
  const [activeTab, setActiveTab] = useState("good"); // "good", "bad", "duplicates"
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();

  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadErrorModalOpen, setUploadErrorModalOpen] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");

  // Fix Record Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [highlightedField, setHighlightedField] = useState(null);

  // Program & Drug List Mapping Detail Modal state (like CAG Configuration)
  const [detailRow, setDetailRow] = useState(null);

  const handleOpenDetailModal = (record) => {
    const activePrograms = PROGRAM_CONFIG.filter((p) => {
      const val = String(record[p.statusKey] || "")
        .trim()
        .toLowerCase();
      return val === "yes" || val === "true";
    }).map((p) => {
      const rawDl = record[p.drugListKey] || "";
      const dlList =
        typeof rawDl === "string"
          ? rawDl
              .split(",")
              .map((s) => s.trim())
              .filter(
                (s) =>
                  s &&
                  s !== "-" &&
                  s.toLowerCase() !== "n/a" &&
                  s.toLowerCase() !== "none",
              )
              .map((s) => ({ label: s }))
          : [];
      return {
        name: p.name,
        drugLists: dlList,
      };
    });
    setDetailRow({ programs: activePrograms });
  };

  // Fetch data for a tab using the API: GET /api/cag/uploads/rows?Filter=bad/good/duplicate
  const loadTabData = async (tab) => {
    setLoading(true);
    showLoading();
    const filterParam = tab === "duplicates" ? "duplicate" : tab;
    const tabSetters = {
      good: setGoodRecords,
      bad: setBadRecords,
      duplicates: setDuplicateRecords,
    };
    try {
      const response = await fetchCagUploadRows(filterParam);
      tabSetters[tab]?.(extractRows(response.data));
    } catch (err) {
      console.warn(`Failed to fetch ${filterParam} upload rows:`, err);
      tabSetters[tab]?.([]);
    } finally {
      setLoading(false);
      hideLoading();
    }
  };

  // Fetch all staging tabs
  const fetchAllStagingRows = useCallback(async () => {
    setLoading(true);
    showLoading();
    try {
      const [badRes, goodRes, dupRes] = await Promise.allSettled([
        fetchCagUploadRows("bad"),
        fetchCagUploadRows("good"),
        fetchCagUploadRows("duplicate"),
      ]);

      const rowsOf = (res) =>
        res.status === "fulfilled" ? extractRows(res.value?.data) : [];
      setBadRecords(rowsOf(badRes));
      setGoodRecords(rowsOf(goodRes));
      setDuplicateRecords(rowsOf(dupRes));
    } catch (err) {
      console.warn("Failed to fetch CAG upload rows:", err);
      setBadRecords([]);
      setGoodRecords([]);
      setDuplicateRecords([]);
    } finally {
      setLoading(false);
      hideLoading();
    }
  }, [showLoading, hideLoading]);

  // Fetch all staging tabs on mount
  useEffect(() => {
    fetchAllStagingRows().catch((err) => console.error(err));
  }, [fetchAllStagingRows]);

  // Handler: Switch Tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedIds([]);
    loadTabData(tab).catch((err) => console.error(err));
  };

  // Handler: Download Template
  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      await downloadCagTemplate();
    } catch (error) {
      console.error("Failed to download CAG template:", error);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Handler: Upload CSV / Excel File
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (
      goodRecords.length > 0 ||
      badRecords.length > 0 ||
      duplicateRecords.length > 0
    ) {
      e.target.value = "";
      return;
    }

    const allowedExtensions = [".csv", ".xlsx", ".xls"];
    const fileExt = "." + file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      setUploadErrorMessage(
        "Invalid file type. Please upload a .csv, .xlsx, or .xls file.",
      );
      setUploadErrorModalOpen(true);
      e.target.value = "";
      return;
    }

    try {
      setUploadingFile(true);
      showLoading();
      await uploadCagFile(file);
      await fetchAllStagingRows();
    } catch (error) {
      console.error("Failed to upload file:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to upload file. Please try again.";
      setUploadErrorMessage(errorMessage);
      setUploadErrorModalOpen(true);
    } finally {
      setUploadingFile(false);
      hideLoading();
      e.target.value = "";
    }
  };

  // Helper to get error message for a form field
  const getFieldErrorMessage = (fieldName) => {
    if (!editingRecord) return "";
    const currentVal = editFormData[fieldName];
    const originalVal = editingRecord[fieldName];
    const isTouchedOrChanged = currentVal !== originalVal;

    // Check required field rules if currently empty
    if (
      (fieldName === "carrierId" ||
        fieldName === "groupName" ||
        fieldName === "groupStartDate") &&
      (!currentVal || !String(currentVal).trim())
    ) {
      const requiredMessages = {
        carrierId: "Carrier ID is required",
        groupName: "Group Name is required",
        groupStartDate: "Group Start Date is required",
      };
      return requiredMessages[fieldName];
    }

    // If user hasn't modified this field yet or it still equals the erroneous value, show record error
    if (!isTouchedOrChanged || currentVal === originalVal) {
      const recordError = getFieldError(editingRecord.errors, fieldName);
      if (recordError?.message) {
        return recordError.message;
      }
    }

    return "";
  };

  // Handler: Fix Bad Record / Resolve Errors Modal
  const handleOpenEditModal = (record, targetField = null) => {
    setEditingRecord(record);

    let initialFocusField = targetField;
    if (!initialFocusField) {
      initialFocusField = resolveInitialFocusField(record);
    }

    setHighlightedField(initialFocusField || null);
    setEditFormData(buildEditFormData(record));
    setEditModalOpen(true);
  };

  const formatDateForBackend = (val) => {
    if (!val) return "";
    if (typeof val === "string") {
      const trimmed = val.trim();
      if (!trimmed) return "";
      if (trimmed.includes("T")) return trimmed.split("T")[0];
      if (trimmed.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        return trimmed.slice(0, 10);
      }
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
        const [m, d, y] = trimmed.split("/");
        return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      }
    }
    const d = dayjs(val);
    return d.isValid() ? d.format("YYYY-MM-DD") : val;
  };

  const handleSaveFixedRecord = async () => {
    if (!editingRecord) return;

    const hasCarrierIdError = !editFormData.carrierId?.trim();
    const hasGroupNameError = !editFormData.groupName?.trim();
    const hasStartDateError = !editFormData.groupStartDate?.trim();

    if (hasCarrierIdError || hasGroupNameError || hasStartDateError) {
      return;
    }

    const rowPayload = buildSavePayload(
      editingRecord,
      editFormData,
      formatDateForBackend,
    );

    try {
      showLoading();
      await updateCagUploadRow({ rows: [rowPayload] });
      setEditModalOpen(false);
      setEditingRecord(null);
      setHighlightedField(null);
      await fetchAllStagingRows();
    } catch (err) {
      console.error("Failed to update staging row via PUT API:", err);
    } finally {
      hideLoading();
    }
  };

  // Handler: Commit/Publish Selected Records (Good Records)
  const handlePublishSelected = async () => {
    const idsToCommit =
      selectedIds.length > 0 ? selectedIds : goodRecords.map((r) => r.id);

    if (idsToCommit.length === 0) return;

    const numericIds = idsToCommit.map(toNumericId);

    try {
      showLoading();
      await commitCagUploadRows({ rowIds: numericIds });
      setSelectedIds([]);
      await fetchAllStagingRows();
    } catch (err) {
      console.error("Failed to commit upload rows via API:", err);
    } finally {
      hideLoading();
    }
  };

  // Handler: Delete Records (Good, Bad, and Duplicate Records)
  const handleDeleteSelected = async () => {
    const recordsByTab = {
      good: goodRecords,
      duplicates: duplicateRecords,
    };
    const currentList = recordsByTab[activeTab] || badRecords;
    const idsToDelete =
      selectedIds.length > 0 ? selectedIds : currentList.map((r) => r.id);
    if (idsToDelete.length === 0) return;

    try {
      showLoading();
      const numericIds = idsToDelete.map(toNumericId);
      await deleteCagUploadRows(numericIds);
      setSelectedIds([]);
      await fetchAllStagingRows();
    } catch (err) {
      console.error("Failed to delete selected rows via API:", err);
    } finally {
      hideLoading();
    }
  };

  // Current displayed records based on tab
  const getCurrentTabRecords = () => {
    if (activeTab === "good") return goodRecords;
    if (activeTab === "bad") return badRecords;
    if (activeTab === "duplicates") return duplicateRecords;
    return [];
  };

  const currentRecords = getCurrentTabRecords();

  const hasStagingRecords =
    (goodRecords?.length || 0) > 0 ||
    (badRecords?.length || 0) > 0 ||
    (duplicateRecords?.length || 0) > 0;

  const columns = useMemo(() => {
    const cols = [];

    cols.push(checkboxColumn);

    if (activeTab !== "good") {
      cols.push({
        id: "actions",
        header: ActionsHeader,
        meta: { onResolve: handleOpenEditModal },
        cell: ActionsCell,
      });
    }

    STAGING_COLUMNS_CONFIG.forEach((col) => {
      if (col.isCombined) {
        cols.push({
          id: col.id,
          header: createHeaderWithSort(col.header, true),
          accessorFn: (row) =>
            `${capitalizeFirstLetter(row[col.primaryKey] || "")} ${row[col.secondaryKey] || ""}`.trim(),
          minWidth: col.minWidth,
          size: col.minWidth,
          minSize: col.minWidth,
          meta: { col, onResolve: handleOpenEditModal },
          cell: CombinedCell,
        });
        return;
      }

      if (col.isPrograms) {
        cols.push({
          id: col.id,
          header: createHeaderWithSort(col.header, true),
          accessorFn: (row) =>
            PROGRAM_CONFIG.filter((p) => isProgramActive(row, p))
              .map((p) => p.name)
              .join(", "),
          minWidth: col.minWidth,
          size: col.minWidth,
          minSize: 240,
          meta: {
            onResolve: handleOpenEditModal,
            onOpenDetail: handleOpenDetailModal,
          },
          cell: ProgramsCell,
        });
        return;
      }

      if (col.isDrugList) {
        cols.push({
          id: col.id,
          header: createHeaderWithSort(col.header, true),
          accessorFn: (row) => collectUniqueDrugLists(row).join(", "),
          minWidth: col.minWidth,
          size: col.minWidth,
          minSize: col.minWidth,
          meta: {
            onResolve: handleOpenEditModal,
            onOpenDetail: handleOpenDetailModal,
          },
          cell: DrugListCell,
        });
        return;
      }

      const isVendorField =
        col.accessorKey === "copayAssistVendor" ||
        col.accessorKey === "intlSourcingVendor";

      cols.push({
        id: col.id,
        header: createHeaderWithSort(col.header, true),
        accessorFn: (row) => {
          const v = row[col.accessorKey];
          if (isVendorField && v) {
            return formatVendorName(String(v));
          }
          return v ?? "";
        },
        minWidth: col.minWidth,
        size: col.minWidth,
        minSize: col.minWidth,
        meta: { col, onResolve: handleOpenEditModal },
        cell: GenericCell,
      });
    });

    return cols;
  }, [activeTab]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F7F8FB",
        overflow: "hidden",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          p: "24px 18px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          transform: `scale(${containerScale})`,
          transformOrigin: "top left",
          width: containerScale < 1 ? "111.1%" : "100%",
          height: "100%",
          overflowX: "hidden",
        }}
      >
        {/* 1. Header & Breadcrumb Bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            margin: "-8px 0 -4px 0",
            minHeight: "40px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "18px",
              fontWeight: 600,
              color: "#080A0E",
              "& .breadcrumb-separator": {
                margin: "0 4px",
                color: "#080A0E",
              },
              "& .breadcrumb-link": {
                background: "none",
                border: 0,
                padding: 0,
                fontFamily: "inherit",
                lineHeight: "inherit",
                color: "#080A0E",
                fontSize: "18px",
                fontWeight: 600,
                cursor: "pointer",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              },
              "& .breadcrumb-current": {
                color: "#080A0E",
                fontSize: "18px",
                fontWeight: 600,
                cursor: "default",
              },
            }}
          >
            <button type="button" className="breadcrumb-link" onClick={onBack}>
              CAG Configuration
            </button>
            <span className="breadcrumb-separator">&gt;</span>
            <span className="breadcrumb-current">Import CAG Configuration</span>
          </Box>

          {/* Top Right Action Buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
              startIcon={<Download size={16} />}
              sx={{
                height: "33px",
                borderRadius: "48px",
                border: "1px solid #E8E8E9",
                color: "#403C6F",
                bgcolor: "#FFFFFF",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                fontStyle: "normal",
                px: 2,
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#403C6F",
                  bgcolor: "rgba(64, 60, 111, 0.04)",
                },
              }}
            >
              {downloadingTemplate ? "Downloading..." : "Download Template"}
            </Button>

            <Tooltip
              title={
                hasStagingRecords
                  ? "Publish or delete the staged records before uploading a new file."
                  : ""
              }
              arrow
              placement="bottom"
            >
              <span>
                <Button
                  component="label"
                  variant="contained"
                  disabled={!canWrite || uploadingFile || hasStagingRecords}
                  startIcon={<Upload size={16} />}
                  sx={{
                    height: "33px",
                    borderRadius: "48px",
                    bgcolor: "#403C6F",
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    fontStyle: "normal",
                    px: 2,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#332F5B",
                      boxShadow: "none",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(64, 60, 111, 0.4)",
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                    "&:focus-visible": {
                      outline: "2px solid #1C6DD0",
                      outlineOffset: "2px",
                    },
                  }}
                >
                  {uploadingFile ? "Uploading..." : "Upload File"}
                  <input
                    type="file"
                    accept=".csv, .xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv"
                    hidden
                    disabled={!canWrite || uploadingFile || hasStagingRecords}
                    onChange={handleFileUpload}
                  />
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* 3. Navigation Tabs Bar */}
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            borderRadius: "12px",
            border: "1px solid #E8E8E9",
            p: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* Good Records Tab */}
            <Button
              onClick={() => handleTabChange("good")}
              sx={tabButtonSx(activeTab === "good", "#12B76A", "#0E9355")}
            >
              Good Records ({goodRecords.length})
            </Button>

            {/* Bad Records Tab */}
            <Button
              onClick={() => handleTabChange("bad")}
              sx={tabButtonSx(activeTab === "bad", "#F04438", "#D92D20")}
            >
              Bad Records ({badRecords.length})
            </Button>

            {/* Duplicate Warnings Tab */}
            <Button
              onClick={() => handleTabChange("duplicates")}
              sx={tabButtonSx(activeTab === "duplicates", "#F79009", "#DC6803")}
            >
              Duplicate Records ({duplicateRecords.length})
            </Button>
          </Box>

          {/* Duplicates Tab: Show Delete button */}
          {activeTab === "duplicates" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button
                variant="outlined"
                disabled={
                  selectedIds.length === 0 ||
                  duplicateRecords.length === 0 ||
                  !canWrite
                }
                onClick={handleDeleteSelected}
                startIcon={<Trash2 size={15} />}
                sx={deleteButtonSx(
                  selectedIds.length === 0 || duplicateRecords.length === 0,
                )}
              >
                Delete Selected ({selectedIds.length})
              </Button>
            </Box>
          )}

          {/* Bad Records Tab: Show Delete button */}
          {activeTab === "bad" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button
                variant="outlined"
                disabled={
                  selectedIds.length === 0 ||
                  badRecords.length === 0 ||
                  !canWrite
                }
                onClick={handleDeleteSelected}
                startIcon={<Trash2 size={15} />}
                sx={deleteButtonSx(
                  selectedIds.length === 0 || badRecords.length === 0,
                )}
              >
                Delete Selected ({selectedIds.length})
              </Button>
            </Box>
          )}

          {/* Good Records Tab: Show both Delete and Publish buttons */}
          {activeTab === "good" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button
                variant="outlined"
                disabled={
                  selectedIds.length === 0 ||
                  goodRecords.length === 0 ||
                  !canWrite
                }
                onClick={handleDeleteSelected}
                startIcon={<Trash2 size={15} />}
                sx={deleteButtonSx(
                  selectedIds.length === 0 || goodRecords.length === 0,
                )}
              >
                Delete Selected ({selectedIds.length})
              </Button>
              <Button
                variant="contained"
                onClick={handlePublishSelected}
                disabled={
                  selectedIds.length === 0 ||
                  goodRecords.length === 0 ||
                  !canWrite
                }
                startIcon={<Check size={15} />}
                sx={{
                  height: "33px",
                  borderRadius: "48px",
                  bgcolor: "#403C6F",
                  color: "#FFFFFF",
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 500,
                  fontStyle: "normal",
                  px: 2.5,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#34315A" },
                  "&.Mui-disabled": {
                    bgcolor: "rgba(64, 60, 111, 0.4)",
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                }}
              >
                Publish Selected ({selectedIds.length})
              </Button>
            </Box>
          )}
        </Box>

        {/* 4. Staging Queue Table using CommonTable */}
        <Box
          ref={resultsContainerRef}
          sx={{
            flexGrow: 1,
            minHeight: 400,
            overflow: "hidden",
            "& .common-table-header-cell": {
              fontSize: "16px !important",
              lineHeight: "24px !important",
              fontWeight: "700 !important",
              letterSpacing: "0px !important",
              color: "#403C6F !important",
              backgroundColor: "#CFCEDA !important",
            },
            "& .common-table-header-cell span": {
              fontSize: "16px !important",
              lineHeight: "24px !important",
              fontWeight: "550 !important",
              letterSpacing: "0px !important",
              color: "#4B3F72 !important",
              fontStyle: "normal !important",
              textTransform: "none !important",
            },
            "& .staging-selected-row": {
              backgroundColor: "#F4EFFE !important",
            },
          }}
        >
          <CommonTable
            columns={columns}
            data={currentRecords}
            loading={loading}
            paginate={false}
            manualSorting={false}
            enableRowSelection={true}
            onRowSelectionChange={setSelectedIds}
            selectedRows={selectedIds}
            getRowClassName={(row) =>
              selectedIds.includes(row.id) ? "staging-selected-row" : ""
            }
            tableHeight={computedTableHeight || "calc(100vh - 250px)"}
            useColumnMinWidth={true}
          />
        </Box>

        {/* 4. Fix Issue / Resolve Errors Dialog */}
        <Dialog
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          maxWidth={false}
          slotProps={{
            paper: {
              sx: {
                maxWidth: "90vw",
                borderRadius: "16px",
                p: { xs: "16px", md: "24px" },
                backgroundColor: "#F7F8FB",
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              m: 0,
              width: "100%",
              height: "40px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                lineHeight: "30px",
                color: "#000000",
              }}
            >
              Resolve Errors
            </Typography>
            <IconButton
              onClick={() => setEditModalOpen(false)}
              aria-label="Close dialog"
            >
              <CloseIcon sx={{ color: placeholderText }} />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: "100%", md: "850px" },
              maxHeight: "75vh",
              overflowY: "auto",
              borderRadius: "16px",
              border: "1px solid #E0E0E0",
              backgroundColor: "#FFFFFF",
              mt: 2,
              p: { xs: 2, md: 3 },
            }}
          >
            {/* Row 1: Org Code & Org Name */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingField
                label="Org Code"
                name="orgCode"
                value={editFormData.orgCode}
                handleChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    orgCode: e.target.value,
                  }))
                }
                placeholder="Enter Org Code (e.g. CPRX)"
                error={Boolean(getFieldErrorMessage("orgCode"))}
                helperText={getFieldErrorMessage("orgCode")}
                highlighted={highlightedField === "orgCode"}
              />
              <StagingField
                label="Org Name"
                name="orgName"
                value={editFormData.orgName}
                handleChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    orgName: e.target.value,
                  }))
                }
                placeholder="Enter Organization Name"
                error={Boolean(getFieldErrorMessage("orgName"))}
                helperText={getFieldErrorMessage("orgName")}
                highlighted={highlightedField === "orgName"}
              />
            </Box>

            {/* Row 2: Carrier Name & Carrier ID */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingField
                label="Carrier Name"
                name="carrierName"
                value={editFormData.carrierName}
                handleChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    carrierName: e.target.value,
                  }))
                }
                placeholder="Enter Carrier Name"
                error={Boolean(getFieldErrorMessage("carrierName"))}
                helperText={getFieldErrorMessage("carrierName")}
                highlighted={highlightedField === "carrierName"}
              />
              <StagingField
                label="Carrier ID"
                name="carrierId"
                value={editFormData.carrierId}
                handleChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    carrierId: e.target.value,
                  }))
                }
                placeholder="Enter Carrier ID"
                error={Boolean(getFieldErrorMessage("carrierId"))}
                helperText={getFieldErrorMessage("carrierId")}
                highlighted={highlightedField === "carrierId"}
              />
            </Box>

            {/* Row 3: Parent Account ID & Parent Account Name */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingField
                label="Parent Account ID"
                name="parentAccountId"
                value={editFormData.parentAccountId}
                handleChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    parentAccountId: e.target.value,
                  }))
                }
                placeholder="Enter Parent Account ID"
                error={Boolean(getFieldErrorMessage("parentAccountId"))}
                helperText={getFieldErrorMessage("parentAccountId")}
                highlighted={highlightedField === "parentAccountId"}
              />
              <StagingField
                label="Parent Account Name"
                name="parentAccountName"
                value={editFormData.parentAccountName}
                handleChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    parentAccountName: e.target.value,
                  }))
                }
                placeholder="Enter Parent Account Name"
                error={Boolean(getFieldErrorMessage("parentAccountName"))}
                helperText={getFieldErrorMessage("parentAccountName")}
                highlighted={highlightedField === "parentAccountName"}
              />
            </Box>

            {/* Row 4: Group Name & Group Number */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingField
                label="Group Name"
                name="groupName"
                value={editFormData.groupName}
                handleChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    groupName: e.target.value,
                  }))
                }
                placeholder="Enter Group Name"
                error={Boolean(getFieldErrorMessage("groupName"))}
                helperText={getFieldErrorMessage("groupName")}
                highlighted={highlightedField === "groupName"}
              />
              <StagingField
                label="Group Number"
                name="groupNumber"
                value={editFormData.groupNumber}
                handleChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    groupNumber: e.target.value,
                  }))
                }
                placeholder="Enter Group Number"
                error={Boolean(getFieldErrorMessage("groupNumber"))}
                helperText={getFieldErrorMessage("groupNumber")}
                highlighted={highlightedField === "groupNumber"}
              />
            </Box>

            {/* Row 5: Group Start Date & Group End Date */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingDateField
                label="Group Start Date"
                name="groupStartDate"
                value={editFormData.groupStartDate}
                onChange={(newVal) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    groupStartDate: newVal,
                  }))
                }
                error={Boolean(getFieldErrorMessage("groupStartDate"))}
                helperText={getFieldErrorMessage("groupStartDate")}
                highlighted={highlightedField === "groupStartDate"}
              />
              <StagingDateField
                label="Group End Date"
                name="groupEndDate"
                value={editFormData.groupEndDate}
                onChange={(newVal) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    groupEndDate: newVal,
                  }))
                }
                error={Boolean(getFieldErrorMessage("groupEndDate"))}
                helperText={getFieldErrorMessage("groupEndDate")}
                highlighted={highlightedField === "groupEndDate"}
              />
            </Box>

            {/* Row 6: Care Guide Effective Date & PCA Directive */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingDateField
                label="Care Guide Effective Date"
                name="careGuideEffectiveDate"
                value={editFormData.careGuideEffectiveDate}
                onChange={(newVal) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    careGuideEffectiveDate: newVal,
                  }))
                }
                error={Boolean(getFieldErrorMessage("careGuideEffectiveDate"))}
                helperText={getFieldErrorMessage("careGuideEffectiveDate")}
                highlighted={highlightedField === "careGuideEffectiveDate"}
              />
              <StagingField
                label="PCA Directive"
                name="pcaDirective"
                value={editFormData.pcaDirective}
                handleChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    pcaDirective: e.target.value,
                  }))
                }
                placeholder="Enter PCA Directive"
                error={Boolean(getFieldErrorMessage("pcaDirective"))}
                helperText={getFieldErrorMessage("pcaDirective")}
                highlighted={highlightedField === "pcaDirective"}
              />
            </Box>

            {/* Row 8: Copay Assist Status & Drug Lists & Vendor */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingSelectField
                label="Copay Assist Status"
                name="copayAssistStatus"
                value={editFormData.copayAssistStatus}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    copayAssistStatus: val,
                  }))
                }
                error={Boolean(getFieldErrorMessage("copayAssistStatus"))}
                helperText={getFieldErrorMessage("copayAssistStatus")}
                highlighted={highlightedField === "copayAssistStatus"}
              />
              <StagingSelectField
                label="Copay Assist Drug Lists"
                name="copayAssistDrugLists"
                value={editFormData.copayAssistDrugLists}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    copayAssistDrugLists: val,
                  }))
                }
                multiple={true}
                options={getProgramDrugListOptions("Copay Assist")}
                disabled={editFormData.copayAssistStatus === "No"}
                placeholder="Select Drug Lists"
                error={Boolean(getFieldErrorMessage("copayAssistDrugLists"))}
                helperText={getFieldErrorMessage("copayAssistDrugLists")}
                highlighted={highlightedField === "copayAssistDrugLists"}
              />
              <StagingSelectField
                label="Copay Assist Vendor"
                name="copayAssistVendor"
                value={editFormData.copayAssistVendor}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    copayAssistVendor: val,
                  }))
                }
                options={getProgramVendorOptions("Copay Assist")}
                disabled={editFormData.copayAssistStatus === "No"}
                placeholder="Select Vendor"
                error={Boolean(getFieldErrorMessage("copayAssistVendor"))}
                helperText={getFieldErrorMessage("copayAssistVendor")}
                highlighted={highlightedField === "copayAssistVendor"}
              />
            </Box>

            {/* Row 9: ECMP Status & Drug Lists */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingSelectField
                label="ECMP Status"
                name="ecmpStatus"
                value={editFormData.ecmpStatus}
                onChange={(val) =>
                  setEditFormData((prev) => ({ ...prev, ecmpStatus: val }))
                }
                error={Boolean(getFieldErrorMessage("ecmpStatus"))}
                helperText={getFieldErrorMessage("ecmpStatus")}
                highlighted={highlightedField === "ecmpStatus"}
              />
              <StagingSelectField
                label="ECMP Drug Lists"
                name="ecmpDrugLists"
                value={editFormData.ecmpDrugLists}
                onChange={(val) =>
                  setEditFormData((prev) => ({ ...prev, ecmpDrugLists: val }))
                }
                multiple={true}
                options={getProgramDrugListOptions("ECMP")}
                disabled={editFormData.ecmpStatus === "No"}
                placeholder="Select Drug Lists"
                error={Boolean(getFieldErrorMessage("ecmpDrugLists"))}
                helperText={getFieldErrorMessage("ecmpDrugLists")}
                highlighted={highlightedField === "ecmpDrugLists"}
              />
            </Box>

            {/* Row 10: GLP-1 Status & Drug Lists */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingSelectField
                label="GLP-1 Status"
                name="glp1Status"
                value={editFormData.glp1Status}
                onChange={(val) =>
                  setEditFormData((prev) => ({ ...prev, glp1Status: val }))
                }
                error={Boolean(getFieldErrorMessage("glp1Status"))}
                helperText={getFieldErrorMessage("glp1Status")}
                highlighted={highlightedField === "glp1Status"}
              />
              <StagingSelectField
                label="GLP-1 Drug Lists"
                name="glp1DrugLists"
                value={editFormData.glp1DrugLists}
                onChange={(val) =>
                  setEditFormData((prev) => ({ ...prev, glp1DrugLists: val }))
                }
                multiple={true}
                options={getProgramDrugListOptions("GLP-1")}
                disabled={editFormData.glp1Status === "No"}
                placeholder="Select Drug Lists"
                error={Boolean(getFieldErrorMessage("glp1DrugLists"))}
                helperText={getFieldErrorMessage("glp1DrugLists")}
                highlighted={highlightedField === "glp1DrugLists"}
              />
            </Box>

            {/* Row 11: International Sourcing Status & Drug Lists & Vendor */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingSelectField
                label="International Sourcing Status"
                name="intlSourcingStatus"
                value={editFormData.intlSourcingStatus}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    intlSourcingStatus: val,
                  }))
                }
                error={Boolean(getFieldErrorMessage("intlSourcingStatus"))}
                helperText={getFieldErrorMessage("intlSourcingStatus")}
                highlighted={highlightedField === "intlSourcingStatus"}
              />
              <StagingSelectField
                label="International Sourcing Drug Lists"
                name="intlSourcingDrugLists"
                value={editFormData.intlSourcingDrugLists}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    intlSourcingDrugLists: val,
                  }))
                }
                multiple={true}
                options={getProgramDrugListOptions("International Sourcing")}
                disabled={editFormData.intlSourcingStatus === "No"}
                placeholder="Select Drug Lists"
                error={Boolean(getFieldErrorMessage("intlSourcingDrugLists"))}
                helperText={getFieldErrorMessage("intlSourcingDrugLists")}
                highlighted={highlightedField === "intlSourcingDrugLists"}
              />
              <StagingSelectField
                label="International Sourcing Vendor"
                name="intlSourcingVendor"
                value={editFormData.intlSourcingVendor}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    intlSourcingVendor: val,
                  }))
                }
                options={getProgramVendorOptions("International Sourcing")}
                disabled={editFormData.intlSourcingStatus === "No"}
                placeholder="Select Vendor"
                error={Boolean(getFieldErrorMessage("intlSourcingVendor"))}
                helperText={getFieldErrorMessage("intlSourcingVendor")}
                highlighted={highlightedField === "intlSourcingVendor"}
              />
            </Box>

            {/* Row 12: PAP Status & Drug Lists */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingSelectField
                label="PAP Status"
                name="papStatus"
                value={editFormData.papStatus}
                onChange={(val) =>
                  setEditFormData((prev) => ({ ...prev, papStatus: val }))
                }
                error={Boolean(getFieldErrorMessage("papStatus"))}
                helperText={getFieldErrorMessage("papStatus")}
                highlighted={highlightedField === "papStatus"}
              />
              <StagingSelectField
                label="PAP Drug Lists"
                name="papDrugLists"
                value={editFormData.papDrugLists}
                onChange={(val) =>
                  setEditFormData((prev) => ({ ...prev, papDrugLists: val }))
                }
                multiple={true}
                options={getProgramDrugListOptions("PAP")}
                disabled={editFormData.papStatus === "No"}
                placeholder="Select Drug Lists"
                error={Boolean(getFieldErrorMessage("papDrugLists"))}
                helperText={getFieldErrorMessage("papDrugLists")}
                highlighted={highlightedField === "papDrugLists"}
              />
            </Box>

            {/* Row 13: Biosimilars Status & Drug Lists */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingSelectField
                label="Biosimilars Status"
                name="biosimilarsStatus"
                value={editFormData.biosimilarsStatus}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    biosimilarsStatus: val,
                  }))
                }
                error={Boolean(getFieldErrorMessage("biosimilarsStatus"))}
                helperText={getFieldErrorMessage("biosimilarsStatus")}
                highlighted={highlightedField === "biosimilarsStatus"}
              />
              <StagingSelectField
                label="Biosimilars Drug Lists"
                name="biosimilarsDrugLists"
                value={editFormData.biosimilarsDrugLists}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    biosimilarsDrugLists: val,
                  }))
                }
                multiple={true}
                options={getProgramDrugListOptions("Biosimilars")}
                disabled={editFormData.biosimilarsStatus === "No"}
                placeholder="Select Drug Lists"
                error={Boolean(getFieldErrorMessage("biosimilarsDrugLists"))}
                helperText={getFieldErrorMessage("biosimilarsDrugLists")}
                highlighted={highlightedField === "biosimilarsDrugLists"}
              />
            </Box>

            {/* Row 14: Women's Health Status & Drug Lists */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={2}
            >
              <StagingSelectField
                label="Women's Health Status"
                name="womensHealthStatus"
                value={editFormData.womensHealthStatus}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    womensHealthStatus: val,
                  }))
                }
                error={Boolean(getFieldErrorMessage("womensHealthStatus"))}
                helperText={getFieldErrorMessage("womensHealthStatus")}
                highlighted={highlightedField === "womensHealthStatus"}
              />
              <StagingSelectField
                label="Women's Health Drug Lists"
                name="womensHealthDrugLists"
                value={editFormData.womensHealthDrugLists}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    womensHealthDrugLists: val,
                  }))
                }
                multiple={true}
                options={getProgramDrugListOptions("Women's Health Program")}
                disabled={editFormData.womensHealthStatus === "No"}
                placeholder="Select Drug Lists"
                error={Boolean(getFieldErrorMessage("womensHealthDrugLists"))}
                helperText={getFieldErrorMessage("womensHealthDrugLists")}
                highlighted={highlightedField === "womensHealthDrugLists"}
              />
            </Box>

            {/* Row 15: Diabetes Status & Drug Lists */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
              width="100%"
              mb={1}
            >
              <StagingSelectField
                label="Diabetes Status"
                name="diabetesStatus"
                value={editFormData.diabetesStatus}
                onChange={(val) =>
                  setEditFormData((prev) => ({ ...prev, diabetesStatus: val }))
                }
                error={Boolean(getFieldErrorMessage("diabetesStatus"))}
                helperText={getFieldErrorMessage("diabetesStatus")}
                highlighted={highlightedField === "diabetesStatus"}
              />
              <StagingSelectField
                label="Diabetes Drug Lists"
                name="diabetesDrugLists"
                value={editFormData.diabetesDrugLists}
                onChange={(val) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    diabetesDrugLists: val,
                  }))
                }
                multiple={true}
                options={getProgramDrugListOptions("Diabetes")}
                disabled={editFormData.diabetesStatus === "No"}
                placeholder="Select Drug Lists"
                error={Boolean(getFieldErrorMessage("diabetesDrugLists"))}
                helperText={getFieldErrorMessage("diabetesDrugLists")}
                highlighted={highlightedField === "diabetesDrugLists"}
              />
            </Box>
          </DialogContent>

          <DialogActions
            sx={{ justifyContent: "flex-end", gap: "10px", mt: 2, p: 0 }}
          >
            <Button
              onClick={() => setEditModalOpen(false)}
              variant="outlined"
              sx={{
                borderColor: "#E8E8E9",
                color: "#6E6D92",
                textTransform: "none",
                fontWeight: 500,
                px: 3,
                borderRadius: "52px",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveFixedRecord}
              variant="contained"
              color="primary"
              sx={{
                borderRadius: "52px",
                backgroundColor: "#403C6F",
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                px: 3,
                letterSpacing: 0,
                "&:hover": { backgroundColor: "#332F5B" },
                "&:focus-visible": {
                  outline: "2px solid #1C6DD0",
                  outlineOffset: "2px",
                },
              }}
            >
              Validate & Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Program & Drug List Mapping Detail Modal */}
        <MappingDetailModal
          open={Boolean(detailRow)}
          onClose={() => setDetailRow(null)}
          data={detailRow}
        />

        {/* Upload Error Popup Modal */}
        <CommonDialog
          open={uploadErrorModalOpen}
          onClose={() => setUploadErrorModalOpen(false)}
          variant="alert"
          icon={
            <ErrorOutlineIcon sx={{ color: "#E53935", fontSize: "32px" }} />
          }
          title="Upload Failed"
          actions={
            <Button
              variant="contained"
              onClick={() => setUploadErrorModalOpen(false)}
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: "14px",
                backgroundColor: "#403C6F",
                color: "#FFF",
                px: 6,
                py: 1,
                boxShadow: "none",
                width: "100%",
                maxWidth: "200px",
                "&:hover": {
                  backgroundColor: "#312e5e",
                  boxShadow: "none",
                },
              }}
            >
              Close
            </Button>
          }
        >
          {uploadErrorMessage || "Failed to upload file. Please try again."}
        </CommonDialog>
      </Box>
    </Box>
  );
};

CagStagingEnvironment.propTypes = {
  onBack: PropTypes.func.isRequired,
  canWrite: PropTypes.bool,
};

export default CagStagingEnvironment;
